import { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useGetCubeMemberMatchFields } from 'services/v1/SystemTenant/AROCube/CubeBuilderService';
import {
  useMemberMappingMembers,
  usePreviewMemberMappingDefinition,
  useSaveMemberMappingDefinition,
} from 'services/v1/SystemTenant/AROCube/MemberMappingDefinitionService';
import {
  MemberMappingDefinition,
  MemberMappingDefinitionInput,
} from 'types/api/SystemTenant/AROCube/MemberMappingDefinitionTypes';
import { emptyMemberMatch, MemberMatchConditions, validMemberMatches } from './MemberMatchConditions';
import { MemberMatchPreview } from './MemberMatchPreview';

export function memberMappingInput(definition: MemberMappingDefinition): MemberMappingDefinitionInput {
  return {
    id: definition.id,
    name: definition.name,
    sourceMemberId: definition.sourceMember.value,
    destinationMemberId: definition.destinationMember.value,
    fieldMatches: definition.fieldMatches,
  };
}
const emptyInput = (): MemberMappingDefinitionInput => ({
  name: '',
  sourceMemberId: '',
  destinationMemberId: '',
  fieldMatches: [emptyMemberMatch()],
});

export function MemberMappingEditor({
  open,
  initial,
  seed,
  lockMembers = false,
  readOnly = false,
  onClose,
  onSaved,
}: {
  open: boolean;
  initial?: MemberMappingDefinition;
  seed?: MemberMappingDefinitionInput;
  lockMembers?: boolean;
  readOnly?: boolean;
  onClose: () => void;
  onSaved: (definition: MemberMappingDefinition) => void;
}) {
  const [input, setInput] = useState<MemberMappingDefinitionInput>(emptyInput);
  const members = useMemberMappingMembers(open);
  const save = useSaveMemberMappingDefinition();
  const preview = usePreviewMemberMappingDefinition();
  const initialSignature = JSON.stringify(initial ? memberMappingInput(initial) : seed ?? emptyInput());
  useEffect(() => {
    if (open) {
      setInput(JSON.parse(initialSignature));
      save.reset();
      preview.reset();
    }
    // Mutation objects change after requests; only a new editor session resets the draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSignature]);
  const source = members.data?.data.find((member) => member.value === input.sourceMemberId);
  const destination = members.data?.data.find((member) => member.value === input.destinationMemberId);
  const sourceFields = useGetCubeMemberMatchFields(
    open
      ? source?.subjectId ??
          (input.sourceMemberId === initial?.sourceMember.value ? initial?.sourceSubjectId : undefined)
      : undefined
  );
  const destinationFields = useGetCubeMemberMatchFields(
    open
      ? destination?.subjectId ??
          (input.destinationMemberId === initial?.destinationMember.value ? initial?.destinationSubjectId : undefined)
      : undefined
  );
  const loadingFields = sourceFields.isLoading || destinationFields.isLoading;
  const fieldsError = sourceFields.isError || destinationFields.isError;
  const validConditions =
    !!source &&
    !!destination &&
    !loadingFields &&
    !fieldsError &&
    validMemberMatches(input.fieldMatches, sourceFields.data?.data ?? [], destinationFields.data?.data ?? []);
  const currentPreview = JSON.stringify(preview.variables) === JSON.stringify(input);
  const immutableMembers = readOnly || lockMembers || (initial?.referencesCount ?? 0) > 0;
  const errorMessage = save.error?.response?.data?.message;
  return (
    <Dialog
      open={open}
      onClose={() => !save.isLoading && onClose()}
      fullWidth
      maxWidth='md'
      aria-labelledby='member-mapping-editor-title'
      PaperProps={{
        sx: {
          m: { xs: 1, sm: 4 },
          width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' },
          maxHeight: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' },
          '& .MuiButton-root': { letterSpacing: 0 },
        },
      }}
    >
      <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={1} sx={{ px: 2, py: 1.5 }}>
        <Typography
          id='member-mapping-editor-title'
          variant='body1'
          component='h2'
          fontWeight='bold'
          sx={{ color: '#3B4797', minWidth: 0 }}
        >
          {readOnly ? 'View' : initial ? 'Edit' : 'Create'} Member Mapping Definition
        </Typography>
        <Tooltip title='Close'>
          <span>
            <IconButton aria-label='Close member mapping editor' onClick={onClose} disabled={save.isLoading}>
              <HighlightOffRoundedIcon sx={{ color: '#98A2AE' }} />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <DialogContent
        sx={{
          bgcolor: '#FBFBFB',
          px: { xs: 2, sm: 3 },
          py: 3,
          borderTop: '1px solid #E3EBF6',
          borderBottom: '1px solid #E3EBF6',
        }}
      >
        <Stack spacing={2}>
          {(initial?.referencesCount ?? 0) > 0 && (
            <Alert severity='info'>
              Used by {initial?.referencesCount} cube attributes. Changes to matching conditions apply to all of them.
              The member trees cannot be changed while in use.
            </Alert>
          )}
          <Stack spacing={0.75}>
            <Typography component='label' htmlFor='member-mapping-name' variant='input-label' fontWeight='bold'>
              Mapping Name
            </Typography>
            <TextField
              id='member-mapping-name'
              size='small'
              fullWidth
              required
              value={input.name}
              disabled={readOnly || save.isLoading}
              onChange={(event) => setInput({ ...input, name: event.target.value })}
              inputProps={{ maxLength: 255 }}
            />
          </Stack>
          {members.isError && (
            <Alert severity='error'>Member trees could not be loaded. Close and reopen the editor to try again.</Alert>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
              <Typography component='label' htmlFor='member-mapping-source' variant='input-label' fontWeight='bold'>
                Source Member Tree
              </Typography>
              <Autocomplete
                id='member-mapping-source'
                size='small'
                fullWidth
                options={members.data?.data ?? []}
                value={source ?? null}
                disabled={immutableMembers || members.isLoading || save.isLoading}
                getOptionLabel={(member) => member.label}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                onChange={(_, member) =>
                  setInput({
                    ...input,
                    sourceMemberId: member?.value ?? '',
                    fieldMatches: input.fieldMatches.map((match) => ({
                      ...match,
                      sourceFieldId: '',
                    })),
                  })
                }
                renderInput={(params) => <TextField {...params} placeholder='Select a member tree' required />}
              />
            </Stack>
            <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                component='label'
                htmlFor='member-mapping-destination'
                variant='input-label'
                fontWeight='bold'
              >
                Destination Member Tree
              </Typography>
              <Autocomplete
                id='member-mapping-destination'
                size='small'
                fullWidth
                options={members.data?.data ?? []}
                value={destination ?? null}
                disabled={immutableMembers || members.isLoading || save.isLoading}
                getOptionLabel={(member) => member.label}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                onChange={(_, member) =>
                  setInput({
                    ...input,
                    destinationMemberId: member?.value ?? '',
                    fieldMatches: input.fieldMatches.map((match) => ({
                      ...match,
                      destinationFieldId: '',
                    })),
                  })
                }
                renderInput={(params) => <TextField {...params} placeholder='Select a member tree' required />}
              />
            </Stack>
          </Stack>
          <Divider sx={{ borderColor: '#E3EBF6' }} />
          <Typography variant='input-label' component='h3' fontWeight='bold'>
            Matching conditions
          </Typography>
          {fieldsError && (
            <Alert severity='error'>Member fields could not be loaded. Close and reopen the editor to try again.</Alert>
          )}
          {source &&
            destination &&
            !loadingFields &&
            !fieldsError &&
            (!sourceFields.data?.data.length || !destinationFields.data?.data.length) && (
              <Alert severity='warning'>
                Both member trees need text or member reference fields before a mapping can be defined.
              </Alert>
            )}
          <MemberMatchConditions
            value={input.fieldMatches}
            onChange={(fieldMatches) => setInput({ ...input, fieldMatches })}
            sourceFields={sourceFields.data?.data ?? []}
            destinationFields={destinationFields.data?.data ?? []}
            disabled={loadingFields || !source || !destination || save.isLoading}
            readOnly={readOnly}
          />
          {new Set(input.fieldMatches.map((match) => `${match.sourceFieldId}:${match.destinationFieldId}`)).size !==
            input.fieldMatches.length && <Alert severity='warning'>Each field condition must be different.</Alert>}
          <LoadingButton
            sx={{ alignSelf: 'flex-start' }}
            variant='secondary-table-panel'
            startIcon={<VisibilityOutlinedIcon />}
            loading={preview.isLoading && currentPreview}
            disabled={!validConditions}
            onClick={() => preview.mutate(input)}
          >
            Preview Member Matches
          </LoadingButton>
          {currentPreview && preview.isError && (
            <Alert severity='error'>
              {preview.error?.response?.data?.message || 'Unable to preview member matches. Try again.'}
            </Alert>
          )}
          {currentPreview && preview.data && <MemberMatchPreview preview={preview.data.data} />}
          {save.isError && (
            <Alert severity='error'>
              {typeof errorMessage === 'string' ? errorMessage : 'Unable to save the member mapping definition.'}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <Stack direction='row' justifyContent='flex-end' spacing={2} sx={{ px: 2, py: 2 }}>
        <Button
          variant='main-table-panel-border'
          startIcon={<CloseOutlinedIcon />}
          disabled={save.isLoading}
          onClick={onClose}
        >
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
        {!readOnly && (
          <LoadingButton
            variant='main-table-panel'
            startIcon={<SaveOutlinedIcon />}
            loading={save.isLoading}
            disabled={!input.name.trim() || !validConditions}
            onClick={() =>
              save.mutate({ ...input, name: input.name.trim() }, { onSuccess: (response) => onSaved(response.data) })
            }
          >
            Save Mapping
          </LoadingButton>
        )}
      </Stack>
    </Dialog>
  );
}
